import { RESET_ALL as RESET_ON_LOGOUT } from 'redux-store/reducers/common-reducer';

import { createReducer } from 'utils/redux';
import { API, post } from 'utils/api';
import { logError } from 'utils/log';
import { TOP_INFLUENCERS_KPI_OPTIONS_INSTAGRAM } from 'modules/insights/sov/utils/sov';

import type {
  Action,
  Dispatch,
  GetState,
  SOVReportType,
  SOVTopInfluencers as SOVTopInfluencersResults,
  ThunkAction
} from 'types';
import type { SOVReport } from 'modules/insights/sov/types';

const { SOV } = API.END_POINTS;

const TOP_INFLUENCERS_LOADING = 'sov/TOP_INFLUENCERS_LOADING';
const TOP_INFLUENCERS_SORT_LOADING = 'sov/TOP_INFLUENCERS_SORT_LOADING';
const TOP_INFLUENCERS_LOADED = 'sov/TOP_INFLUENCERS_LOADED';
const TOP_INFLUENCERS_RESET = 'sov/TOP_INFLUENCERS_RESET';
const TOP_INFLUENCERS_SORT = 'sov/TOP_INFLUENCERS_SORT';
const { posts, impressions, engagements, followers } = TOP_INFLUENCERS_KPI_OPTIONS_INSTAGRAM;

export type TopInfluencersState = {
  topInfluencers: Array<SOVTopInfluencersResults>,
  isLoading: boolean,
  isSorting: boolean,
  sort: typeof posts | typeof impressions | typeof engagements | typeof followers
};

type LoadingTypes = typeof TOP_INFLUENCERS_SORT_LOADING | typeof TOP_INFLUENCERS_LOADING;

const initialState: TopInfluencersState = {
  topInfluencers: [],
  isLoading: false,
  isSorting: false,
  sort: posts
};

const setLoadingStatus = (type: LoadingTypes, status: boolean): Action => ({
  type,

  payload: {
    status
  }
});

const topInfluencersLoaded = (results: Array<SOVTopInfluencersResults>): Action => ({
  type: TOP_INFLUENCERS_LOADED,

  payload: {
    results
  }
});

export const resetTopInfluencers = (): Action => ({
  type: TOP_INFLUENCERS_RESET
});

export const sortTopInfluencers = (sort: string): Action => ({
  type: TOP_INFLUENCERS_SORT,

  payload: {
    sort
  }
});

export const fetchTopInfluencers = (sortChange: boolean = false, reportType: SOVReportType): ThunkAction => async (dispatch: Dispatch, getState: GetState) => {
  const loadingType = sortChange ? TOP_INFLUENCERS_SORT_LOADING : TOP_INFLUENCERS_LOADING;
  const state = getState();
  const report: Partial<SOVReport> = state.sovReports.report;
  const { sort } = state.sovTopInfluencers;
  const data = {
    ...report,
    topInfluencersLimit: 20,
    sort
  };

  dispatch(setLoadingStatus(loadingType, true));

  try {
    const url = SOV.getTopInfluencers(data.id, reportType);
    const response = await post(url, { data });
    const topInfluencers = response.data || [];

    dispatch(topInfluencersLoaded(topInfluencers));
    dispatch(setLoadingStatus(loadingType, false));
  } catch (e) {
    dispatch(setLoadingStatus(loadingType, false));
    logError(e);
  }
};


export default createReducer<TopInfluencersState, any>('sovTopInfluencers', initialState, {
  [RESET_ON_LOGOUT]: (): TopInfluencersState => initialState,
  [TOP_INFLUENCERS_LOADING]: (state, { status }): TopInfluencersState => ({
    ...state,
    isLoading: status
  }),
  [TOP_INFLUENCERS_SORT_LOADING]: (state, { status }): TopInfluencersState => ({
    ...state,
    isSorting: status
  }),
  [TOP_INFLUENCERS_LOADED]: (state, { results }): TopInfluencersState => ({
    ...state,
    topInfluencers: results
  }),
  [TOP_INFLUENCERS_RESET]: (): TopInfluencersState => initialState,
  [TOP_INFLUENCERS_SORT]: (state, { sort }): TopInfluencersState => ({
    ...state,
    sort
  })
});
